/**
 * Capsule View Tracking Utilities
 * Tracks views for capsules using Supabase Edge Functions
 */

import { supabase } from '../lib/supabase';

// Get or create anonymous ID for view tracking
const getAnonymousId = () => {
  let anonId = localStorage.getItem('outsera_anon_id');
  if (!anonId) {
    anonId = `anon_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    localStorage.setItem('outsera_anon_id', anonId);
  }
  return anonId;
};

/**
 * Track a view for a capsule using Supabase Edge Function
 * @param {string} capsuleId - The capsule ID to track
 * @param {string|null} viewerWallet - The viewer's wallet address (optional)
 * @returns {Promise<{success: boolean, stats?: object, error?: string}>}
 */
export const trackCapsuleView = async (capsuleId, viewerWallet = null) => {
  try {
    const anonId = getAnonymousId();
    
    console.log('📊 Tracking capsule view:', {
      capsuleId,
      viewerWallet: viewerWallet || 'anonymous',
      anonId: anonId.substring(0, 20) + '...',
    });

    // Call Supabase Edge Function
    const { data, error } = await supabase.functions.invoke('track-capsule-view', {
      body: {
        capsule_id: capsuleId,
        viewer_wallet: viewerWallet,
        anon_id: anonId,
      },
    });

    if (error) {
      // Try to parse the error response for cooldown messages
      const errorContext = error.context || {};
      const statusCode = errorContext.status;
      
      // Check if it's a 429 (cooldown) error - this is normal, not an error
      if (statusCode === 429 || error.message?.includes('429') || error.message?.includes('Too Many Requests')) {
        console.log('⏰ Capsule view cooldown active (already viewed within 30 minutes)');
        return { 
          success: false, 
          error: 'Cooldown active (already viewed within 30 minutes)' 
        };
      }
      
      console.error('❌ Capsule view tracking error:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to track capsule view' 
      };
    }

    if (data?.success) {
      console.log('✅ Capsule view tracked successfully:', data.stats);
      return {
        success: true,
        stats: data.stats,
      };
    } else {
      console.log('⏭️ Capsule view not tracked:', data?.message || 'Unknown reason');
      return {
        success: false,
        error: data?.message || 'View not recorded (cooldown)',
      };
    }
  } catch (error) {
    console.error('❌ Unexpected error tracking capsule view:', error);
    return {
      success: false,
      error: error.message || 'Unexpected error',
    };
  }
};

/**
 * Get view stats for a specific capsule
 * @param {string} capsuleId - The capsule ID
 * @returns {Promise<object>}
 */
export const getCapsuleViewStats = async (capsuleId) => {
  try {
    const { data, error } = await supabase
      .rpc('get_capsule_view_stats', { p_capsule_id: capsuleId });

    if (error) {
      console.error('Error getting capsule view stats:', error);
      return {
        views_24h: 0,
        views_7d: 0,
        total_views: 0,
        unique_viewers_24h: 0,
        trending_score: 0,
      };
    }

    return data || {
      views_24h: 0,
      views_7d: 0,
      total_views: 0,
      unique_viewers_24h: 0,
      trending_score: 0,
    };
  } catch (error) {
    console.error('Unexpected error getting capsule view stats:', error);
    return {
      views_24h: 0,
      views_7d: 0,
      total_views: 0,
      unique_viewers_24h: 0,
      trending_score: 0,
    };
  }
};

